import React from 'react';
import useContact from '../../hooks/useContact';

const WhatsAppIcon = () => (
  <svg className="w-5 h-5 ml-2 text-green-500" fill="currentColor" viewBox="0 0 24 24">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.031-.967-.273-.099-.472-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.149-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.447-.52.149-.174.198-.298.298-.497.099-.198.05-.372-.025-.521-.075-.149-.669-1.611-.916-2.206-.242-.579-.487-.5-.669-.51-.173-.007-.372-.009-.571-.009-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.099 3.2 5.077 4.363.71.306 1.263.489 1.695.626.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.288.173-1.413-.074-.124-.272-.198-.57-.347z"/>
    <path d="M20.52 3.48A11.958 11.958 0 0012 0C5.373 0 0 5.373 0 12c0 2.12.553 4.164 1.6 5.96L0 24l6.26-1.64A11.96 11.96 0 0012 24c6.627 0 12-5.373 12-12 0-3.193-1.247-6.197-3.48-8.52zM12 22c-1.798 0-3.543-.48-5.06-1.39l-.36-.21-3.72.98.99-3.63-.23-.37C2.48 15.54 2 13.8 2 12 2 6.486 6.486 2 12 2c3.18 0 6.167 1.243 8.44 3.51A9.96 9.96 0 0122 12c0 5.514-4.486 10-10 10z"/>
  </svg>
);

const PhoneIcon = () => (
  <svg className="w-5 h-5 ml-2 text-blue-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H5a2 2 0 01-2-2V5zm0 0v14a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5a2 2 0 00-2 2v2zm14-2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5a2 2 0 012-2h2zm0 0v14a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2h-2a2 2 0 00-2 2v2z" />
  </svg>
);

const EmailIcon = () => (
  <svg className="w-5 h-5 ml-2 text-red-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M16 12H8m8 0a4 4 0 11-8 0 4 4 0 018 0zm0 0v4a4 4 0 01-8 0v-4" />
  </svg>
);

const Footer: React.FC = () => {
  const { contactInfo, isLoading: contactLoading } = useContact();

  return (
    <section className="relative py-20 overflow-hidden bg-slate-900" dir="rtl">
      {/* Background pattern */}
      <div className="absolute inset-0" style={{
        backgroundImage: `url('data:image/svg+xml,%3Csvg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"%3E%3Cpath d="M1.22676 0C1.91374 0 2.45351 0.539773 2.45351 1.22676C2.45351 1.91374 1.91374 2.45351 1.22676 2.45351C0.539773 2.45351 0 1.91374 0 1.22676C0 0.539773 0.539773 0 1.22676 0Z" fill="rgba(255,255,255,0.07)"%3E%3C/path%3E%3C/svg%3E')`,
        opacity: 0.1
      }} />
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-2 gap-16 items-center">
          {/* CTA Content */}
          <div className="text-center md:text-right">
            <div className="inline-flex items-center px-4 py-2 bg-teal-500/10 rounded-full mb-6">
              <span className="text-teal-300 text-sm font-medium">نحن هنا لمساعدتك</span>
              <div className="w-2 h-2 rounded-full bg-teal-400 mr-2 animate-pulse" />
            </div>
            <h2 className="text-4xl font-bold text-white mb-6 font-tajawal leading-tight">
              هل أنت مستعد لتحسين
              <span className="text-teal-300"> مهاراتك </span>
              في اللغة الإنجليزية؟
            </h2>
            <p className="text-lg text-gray-300 mb-8 font-cairo">
              انضم إلى الطلاب الذين حسّنوا درجاتهم بأنفسهم من خلال طرقنا المجربة.
            </p>
            <div className="flex flex-col sm:flex-row justify-center md:justify-start gap-4">
              <a href="/register" 
                 className="inline-flex items-center px-8 py-3 rounded-xl bg-teal-500 text-white font-bold text-lg shadow-lg hover:bg-teal-600 transition-all group">
                سجل الآن
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </a>
              <a href="/about" 
                 className="inline-flex items-center px-8 py-3 rounded-xl bg-transparent text-white font-bold text-lg border-2 border-gray-600 hover:border-gray-400 transition-all">
                تعرف علينا
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div className="grid grid-cols-2 gap-8 text-white">
            <div>
              <h3 className="text-xl font-bold mb-4 font-tajawal">روابط سريعة</h3>
              <ul className="space-y-3 font-cairo">
                <li><a href="/" className="text-gray-400 hover:text-teal-300 transition-colors">الرئيسية</a></li>
                <li><a href="/#courses-section" className="text-gray-400 hover:text-teal-300 transition-colors">الدورات</a></li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-bold mb-4 font-tajawal">تواصل معنا</h3>
              <ul className="space-y-3 font-cairo">
                {contactLoading ? (
                  <li className="flex items-center">
                    <div className="w-5 h-5 bg-gray-400 rounded animate-pulse ml-2"></div>
                    <span className="text-gray-400">جاري التحميل...</span>
                  </li>
                ) : (
                  <>
                    {contactInfo?.whatsApp_Number && (
                      <li>
                        <a
                          href={`https://wa.me/${contactInfo.whatsApp_Number}?text=مرحباً، أريد الاستفسار عن الدورات`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="group relative inline-flex items-center gap-2 p-3 bg-gradient-to-br from-green-500 to-green-600 rounded-xl hover:from-green-600 hover:to-green-700 transform hover:scale-110 transition-all duration-300 shadow-lg hover:shadow-xl text-white font-bold"
                          style={{ minWidth: '160px' }}
                        >
                          <WhatsAppIcon />
                          <span className="ltr:ml-2 rtl:mr-2 select-all">{contactInfo.whatsApp_Number}</span>
                          <div className="absolute inset-0 bg-white/20 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                        </a>
                      </li>
                    )}
                    {!contactInfo?.whatsApp_Number && (
                      <li className="flex items-center">
                        <svg className="w-5 h-5 ml-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                        <span className="text-gray-500">غير متاح</span>
                      </li>
                    )}
                  </>
                )}
              </ul>
              <div className="flex space-x-4 mt-6">
                {contactInfo?.facebook_Page && (
                  <a 
                    href={contactInfo.facebook_Page}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group relative p-3 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl hover:from-blue-600 hover:to-blue-700 transform hover:scale-110 transition-all duration-300 shadow-lg hover:shadow-xl"
                  >
                    <span className="sr-only">Facebook</span>
                    <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" />
                    </svg>
                    <div className="absolute inset-0 bg-white/20 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </a>
                )}
                {contactInfo?.youTube_Channel && (
                  <a 
                    href={contactInfo.youTube_Channel}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group relative p-3 bg-gradient-to-br from-red-500 to-red-600 rounded-xl hover:from-red-600 hover:to-red-700 transform hover:scale-110 transition-all duration-300 shadow-lg hover:shadow-xl"
                  >
                    <span className="sr-only">YouTube</span>
                    <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                    </svg>
                    <div className="absolute inset-0 bg-white/20 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </a>
                )}
                {contactInfo?.tiktokChannel && (
                  <a 
                    href={contactInfo.tiktokChannel}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group relative p-3 bg-gradient-to-br from-pink-500 via-purple-500 to-cyan-500 rounded-xl hover:from-pink-600 hover:via-purple-600 hover:to-cyan-600 transform hover:scale-110 transition-all duration-300 shadow-lg hover:shadow-xl"
                  >
                    <span className="sr-only">TikTok</span>
                    <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.7-1.35 3.9-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
                    </svg>
                    <div className="absolute inset-0 bg-white/20 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </a>
                )}
                {!contactInfo?.facebook_Page && !contactInfo?.youTube_Channel && !contactInfo?.tiktokChannel && (
                  <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 bg-gray-400 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                      </svg>
                    </div>
                    <span className="text-gray-400 text-sm">لا توجد روابط متاحة</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        
        <div className="mt-16 pt-8 border-t border-gray-800 text-center">
          <a
            href="https://www.facebook.com/share/16zxvPCUCo/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 text-sm font-cairo font-bold bg-gradient-to-r from-teal-400 via-blue-400 to-purple-500 bg-clip-text text-transparent transition-all duration-300 hover:underline hover:text-teal-600 focus:outline-none focus:ring-2 focus:ring-teal-400 focus:ring-offset-2"
            style={{ direction: 'rtl', padding: '0.5rem 1.5rem', borderRadius: '1rem', letterSpacing: '0.02em' }}
            aria-label="EduCraft Facebook Page"
          >
            <svg
              className="w-6 h-6 md:w-5 md:h-5 text-blue-600 rtl:ml-2 ltr:mr-2 transition-transform duration-300 group-hover:scale-110 group-hover:text-blue-500"
              fill="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
              focusable="false"
              style={{ minWidth: '1.5rem', minHeight: '1.5rem' }}
            >
              <path d="M22.675 0h-21.35C.595 0 0 .592 0 1.326v21.348C0 23.408.595 24 1.325 24h11.495v-9.294H9.692v-3.622h3.128V8.413c0-3.1 1.893-4.788 4.659-4.788 1.325 0 2.463.099 2.797.143v3.24l-1.918.001c-1.504 0-1.797.715-1.797 1.763v2.313h3.587l-.467 3.622h-3.12V24h6.116C23.406 24 24 23.408 24 22.674V1.326C24 .592 23.406 0 22.675 0" />
            </svg>
            <span>© 2025 جميع الحقوق محفوظة | تم التطوير بواسطة فريق EduCraft</span>
          </a>
        </div>
      </div>
    </section>
  );
};

export default Footer; 